import { curveMonotoneX } from '@visx/curve';
import { AreaStack as VisxAreaStack, LinePath } from '@visx/shape';
import { ScaleLinear } from 'd3-scale';
import { Fragment } from 'react';

import { LinearGradient } from '@visx/gradient';

import { generateStackedChartDataByKeys } from './utils';
import { CategoryData } from '../grouped-chart/types';
import { increaseBrightness } from '../utils';
import { CategoryScale } from './types';

type AreaStackProps = {
  data: CategoryData;
  keys: string[];
  valueScale: ScaleLinear<number, number>;
  categoryScale: CategoryScale;
  highlightKey?: string;
};

export const AreaStack = (props: AreaStackProps) => {
  const { data, keys, categoryScale, valueScale, highlightKey } = props;

  const stackedData = generateStackedChartDataByKeys(data, keys);

  return (
    <>
      {data.series.map(({ key, color }) => (
        <LinearGradient
          key={key}
          id={`gradient-${color}`}
          from={color}
          to={color}
          fromOpacity={0.8}
          toOpacity={0.2}
        />
      ))}
      <VisxAreaStack
        keys={keys}
        data={stackedData.items}
        order="reverse"
        x={(d) =>
          (categoryScale(d.data.categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2
        }
        y0={(d) => valueScale(d[0])}
        y1={(d) => valueScale(d[1]) ?? valueScale.range()[1]}
        curve={curveMonotoneX}>
        {({ stacks, path }) =>
          stacks.map((stack) => {
            const color = data.series.find(({ key }) => key === stack.key)?.color ?? '';
            const isHighlighted = highlightKey === stack.key || highlightKey === undefined;
            return (
              <Fragment key={`stack-${stack.key}`}>
                <path
                  d={path(stack) ?? ''}
                  fill={`url(#gradient-${color})`}
                  fillOpacity={isHighlighted ? 1 : 0.3}
                />
                <LinePath
                  data={stack.map((item) => ({
                    categoryValue: item.data.categoryValue,
                    value: item.at(1) ?? 0,
                  }))}
                  x={({ categoryValue }) =>
                    (categoryScale(categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2
                  }
                  y={({ value }) => valueScale(value) ?? 0}
                  stroke={increaseBrightness(color, 20)}
                  strokeOpacity={isHighlighted ? 1 : 0.3}
                  curve={curveMonotoneX}
                />
              </Fragment>
            );
          })
        }
      </VisxAreaStack>
    </>
  );
};
